import type { SimCard, SimCardStates } from '@racemap/sdk/schema/simCard';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { useMemo } from 'react';
import useSWRInfinite, { type SWRInfiniteResponse } from 'swr/infinite';

const apiClient = RacemapAPIClient.fromWindowLocation();

interface UseSimsParams {
  search?: string;
  userId?: string;
  state?: SimCardStates;
}
/**
 * Returns the sims of a user
 *
 * @returns {sims: Array<SIMS> | undefined, isLoading: boolean, error: Error}
 */

export function useSimCardsPaginated({ search, userId, state }: UseSimsParams): {
  sims: Array<SimCard> | undefined;
  isLoading: boolean;
  error: Error;
  size: number;
  setSize: (size: number) => void;
  mutate: SWRInfiniteResponse<Array<SimCard>, Error>['mutate'];
} {
  const pageSize = 100;
  const getKey = (
    pageIndex: number,
    previousPageData: Array<SimCard>,
  ):
    | [
        string,
        string,
        string | undefined,
        string | undefined,
        SimCardStates | undefined,
        number,
        number,
      ]
    | null => {
    if (previousPageData && !previousPageData.length) return null;

    const skip = pageIndex * pageSize;
    const limit = pageSize;
    return ['GET', 'SIMS', search, userId, state, skip, limit];
  };

  const { data, isLoading, error, size, setSize, mutate } = useSWRInfinite(
    getKey,
    async ([_1, _2, search, userId, state, skip, limit]) => {
      return await apiClient.listSims({ search, userId, state, limit, skip });
    },
    {
      revalidateFirstPage: false,
      refreshInterval: OneSecondInMillis * 30,
      revalidateAll: false,
    },
  );

  const sims = useMemo(() => {
    if (data == null) return undefined;
    const simMap = new Map<string, SimCard>(data.flat().map((s) => [s.id.toHexString(), s]));

    return Array.from(simMap.values());
  }, [data]);

  return { sims, isLoading, error, size, setSize, mutate };
}
